import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { AppInsightsService } from '../services/app-insights.service';

/**
 * publicOnlyGuard
 * Feature: 017-public-landing-page (T102 / AD-005).
 *
 * Allows the public landing page to render for unauthenticated visitors
 * (returns true). Authenticated visitors are bounced straight to `/patients`
 * with a `landing_redirect_authenticated` telemetry event (FR-009 / FR-020 /
 * SC-004 — no flash of public content). Mirrors `CustomAuthGuard`'s
 * isLoading$ wait pattern so the decision is made after Auth0 settles.
 */
export const publicOnlyGuard: CanActivateFn = (): Observable<boolean> => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const appInsights = inject(AppInsightsService);

  return auth.isLoading$.pipe(
    filter(isLoading => !isLoading),
    take(1),
    switchMap(() => auth.isAuthenticated$.pipe(take(1))),
    switchMap(isAuthenticated => {
      if (!isAuthenticated) {
        return of(true);
      }
      appInsights.trackEvent('landing_redirect_authenticated', { target: '/patients' });
      router.navigate(['/patients']);
      return of(false);
    })
  );
};
